import React, { useEffect, useRef, useState } from "react";
import { Cropper } from "react-advanced-cropper";
import { FiCrop, FiSun } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
} from "react-bootstrap";
import { ModalWindow } from "../ModalWindow";
import { applyBrightness, useScreenSize } from "../../utils";
import { useAppContext } from "../../context";
import "react-advanced-cropper/dist/style.css";
import style from "./CanvasWithZoom.module.css";

const EditModal = ({
  editCanvasModal,
  setEditCanvasModal,
  currentCanvasImg,
}) => {
  const { t } = useTranslation();
  const { state, dispatch } = useAppContext();
  const screenSize = useScreenSize();
  const [isCropping, setIsCropping] = useState(false);
  const [croppedImg, setCroppedImg] = useState(null);
  const [canvasDataURL, setCanvasDataURL] = useState(null);
  const modalCanvasRef = useRef(null);

  const resetEdits = () => {
    setCroppedImg(null);
    setCanvasDataURL(null);
    setIsCropping(false);
    dispatch({
      type: "SET_BRIGHTNESS",
      payload: { index: state.index, value: 1 },
    });
  };

  useEffect(() => {
    if (editCanvasModal) {
      resetEdits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCanvasModal, state.index]);

  useEffect(() => {
    if (!isCropping && croppedImg && modalCanvasRef.current) {
      // Cuando se sale del modo crop y hay una imagen croppeada, dibujarla en el canvas
      const ctx = modalCanvasRef.current.getContext("2d");
      modalCanvasRef.current.width = croppedImg.width;
      modalCanvasRef.current.height = croppedImg.height;
      ctx.clearRect(0, 0, croppedImg.width, croppedImg.height);
      ctx.drawImage(croppedImg, 0, 0);
    }
  }, [isCropping, croppedImg]);

  useEffect(() => {
    if (!editCanvasModal || !modalCanvasRef.current) return;

    const modalCanvas = modalCanvasRef.current;
    const ctx = modalCanvas.getContext("2d");

    // Usar la imagen croppeada si existe, sino la original
    const imgToDraw = croppedImg || currentCanvasImg;
    if (!imgToDraw) return;

    // Asegurar tamaño del canvas
    modalCanvas.width = imgToDraw.width;
    modalCanvas.height = imgToDraw.height;

    // Dibujar la imagen
    ctx.clearRect(0, 0, modalCanvas.width, modalCanvas.height);
    ctx.drawImage(imgToDraw, 0, 0);

    // Aplicar brillo guardado (o 1 si no hay)
    const brightness = state.brightnessByIndex[state.index] || 1;
    applyBrightness(ctx, brightness);
  }, [
    editCanvasModal,
    currentCanvasImg,
    croppedImg,
    state.brightnessByIndex,
    state.index,
  ]);

  const hideModal = () => {
    setEditCanvasModal(false);
  };

  const datURLToFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const saveChanges = async () => {
    const canvas = modalCanvasRef.current;
    if (!canvas) return;

    let finalCanvas;

    if (croppedImg) {
      // Si se hizo crop, aplicar brillo al canvas cropeado
      const ctx = croppedImg.getContext("2d");
      if (ctx) {
        const brightness = state.brightnessByIndex[state.index] || 1;
        applyBrightness(ctx, brightness);
      }
      finalCanvas = croppedImg;
    } else {
      // Si no se hizo crop, usá el canvas completo (ya tiene brillo aplicado)
      finalCanvas = canvas;
    }

    const dataURL = finalCanvas.toDataURL("image/png");
    const file = datURLToFile(dataURL, "edited-image.png");

    dispatch({
      type: "SET_CROPPED_IMAGE",
      payload: { index: state.index, croppedImage: file },
    });

    hideModal();
  };

  const handleBrightnessChange = (e) => {
    const brightness = Number(e.target.value);
    dispatch({
      type: "SET_BRIGHTNESS",
      payload: { index: state.index, value: brightness },
    });

    const canvas = modalCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;

    // Usar la imagen croppeada si existe, sino la original
    const imgToDraw = croppedImg || currentCanvasImg;
    if (!imgToDraw) return;

    // Redibujar la imagen base
    canvas.width = imgToDraw.width;
    canvas.height = imgToDraw.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgToDraw, 0, 0);

    // Aplicar brillo
    applyBrightness(ctx, brightness);
  };

  const handleCropClick = () => {
    if (modalCanvasRef.current) {
      setCanvasDataURL(modalCanvasRef.current.toDataURL());
      setIsCropping(true);
    }
  };

  const popover = (
    <Popover id="popover-brightness">
      <Popover.Body>
        <Row className={`p-0 m-0 justify-content-center align-items-center`}>
          <Col className="p-0 d-flex justify-content-center" xs={1} lg={1}>
            <FiSun />
          </Col>
          <Col xs={6} lg={7} className="p-0 d-flex justify-content-center">
            <Form.Range
              type="range"
              min="0.5"
              max="1.5"
              step="0.01"
              className={style.rangeBar}
              value={state.brightnessByIndex[state.index] || 1}
              onChange={(e) => handleBrightnessChange(e)}
            />
          </Col>
          <Col className="p-0 d-flex justify-content-center" xs="auto" lg={1}>
            <FiSun size="1.5rem" />
          </Col>
          <Col className="p-0 d-flex justify-content-center" xs="auto" lg={3}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleBrightnessChange({ target: { value: 1 } })}
              className={`ms-3 ${style.cardsCommandsActionBtn}`}
            >
              {/* <GrRefresh /> */}
              {t("Reset")}
            </Button>
          </Col>
        </Row>
      </Popover.Body>
    </Popover>
  );


  const modifiers = {
    wheel: {
      disabled: true, // 👈 Esto desactiva el zoom con scroll del mouse
    },
  };

  return (
    <ModalWindow
      show={editCanvasModal}
      onHide={hideModal}
      closeButton={true}
      centered={true}
      size="md"
      fullscreen={screenSize < 992 && true}
      title="Quick Edits"
      viewDetailsModal={true}
      id="edit-canvas-modal"
      body={
        <>
          <p className={`${style.modalCanvasTxt} ms-3`}>Optional: crop or adjust brightness before using this image.</p>
          <Row className="justify-content-around align-items-center mt-4 mb-4">
            <Col xs="auto">
              {isCropping ? (
                <Button
                  size="sm"
                  variant="dark"
                  onClick={() => { setIsCropping(false) }}
                  className={style.saveBtn}
                  disabled={croppedImg ? false : true}
                >
                  Apply changes
                </Button>
              ) : (
                <Button
                  variant="dark"
                  size="sm"
                  className={style.editModalBtn}
                  onClick={handleCropClick}
                >
                  <FiCrop /> Crop image
                </Button>
              )}
            </Col>
            <Col xs="auto">
              <OverlayTrigger
                trigger="click"
                placement="bottom-start"
                overlay={popover}
                rootClose
              >
                <Button
                  variant="dark"
                  size="sm"
                  className={style.editModalBtn}
                  disabled={state.images.length > 0 ? false : true}
                >
                  <FiSun /> {t("Brightness")}
                </Button>
              </OverlayTrigger>
            </Col>
            <Col xs="auto">
              <Button
                variant="dark"
                size="sm"
                onClick={resetEdits}
                className={style.editModalBtn}
              >
                Reset Adjustment
              </Button>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex justify-content-center">
              {isCropping ? (
                <Cropper
                  src={canvasDataURL}
                  onChange={(cropper) => {
                    const croppedCanvas = cropper.getCanvas();
                    if (croppedCanvas) {
                      setCroppedImg(croppedCanvas);
                    }
                  }}
                  className={style.customCropper}
                  canvas={true}
                  transitions={false}
                  autoZoom={false}
                  modifiers={modifiers}
                />
              ) : (
                <canvas ref={modalCanvasRef} className={style.modalCanvas} />
              )}
            </Col>
          </Row>
        </>
      }
      footer={
        <>
          <Row className="w-100">
            <Col className="d-flex justify-content-start">
              <Button
                variant="dark"
                size="sm"
                className={style.editModalBtn}
                onClick={hideModal}
              >
                {t("Cancel")}
              </Button>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                variant="dark"
                size="sm"
                className={style.saveBtn}
                onClick={saveChanges}
                disabled={isCropping}
              >
                {t("Apply & Continue")}
              </Button>
            </Col>
          </Row>
        </>
      }
    />
  );
};

export default EditModal;
